import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, MapPin, Heart, AlertTriangle, Map, Hospital, 
  School, ChevronRight, Briefcase, Building2, Car, HeartHandshake, Compass, 
  Sun, Moon, Sunset, CloudSun, X, Info, Droplet, CloudRain, Cloud, Train, CloudLightning, Newspaper 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import toast from "react-hot-toast";

// 🔥 കാറ്റഗറി കോൺഫിഗറേഷൻ 🔥
const categoryStyles = {
  "Tourism": { icon: Map, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
  "Places": { icon: Compass, color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-100" },
  "Labours": { icon: Briefcase, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-100" },
  "Drivers": { icon: Car, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
  "Blood Donors": { icon: Droplet, color: "text-red-600", bg: "bg-red-50", border: "border-red-100" },
  "Charity": { icon: HeartHandshake, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-100" },
  "Health": { icon: Hospital, color: "text-red-600", bg: "bg-red-50", border: "border-red-100" },
  "Education": { icon: School, color: "text-sky-600", bg: "bg-sky-50", border: "border-sky-100" },
  "Administration": { icon: Building2, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
  "Emergency": { icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100" },
  "Train Timings": { icon: Train, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
  "Default": { icon: Info, color: "text-gray-500", bg: "bg-gray-50", border: "border-gray-200" }
};

export default function Home() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [dynamicData, setDynamicData] = useState([]);
  
  const [greeting, setGreeting] = useState("Welcome");
  const [theme, setTheme] = useState("bg-[linear-gradient(110deg,#1E3A8A_0%,#2563EB_50%,#3B82F6_100%)]"); 
  const [textGradient, setTextGradient] = useState("from-yellow-300 to-amber-500"); 
  const [weather, setWeather] = useState({ temp: "--°C", desc: "Loading...", icon: Sun, anim: {}, trans: {} });

  // 1. Dynamic Time-Based Themes
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting("Good Morning");
      setTheme("bg-[linear-gradient(110deg,#ea580c_0%,#f97316_50%,#fbbf24_100%)] shadow-[0_20px_40px_-15px_rgba(234,88,12,0.5)]");
      setTextGradient("from-yellow-100 to-white"); 
    } else if (hour >= 12 && hour < 17) {
      setGreeting("Good Afternoon");
      setTheme("bg-[linear-gradient(110deg,#0284c7_0%,#0ea5e9_50%,#38bdf8_100%)] shadow-[0_20px_40px_-15px_rgba(2,132,199,0.5)]");
      setTextGradient("from-yellow-300 to-amber-400"); 
    } else if (hour >= 17 && hour < 19) {
      setGreeting("Good Evening");
      setTheme("bg-[linear-gradient(110deg,#7e22ce_0%,#a855f7_50%,#ec4899_100%)] shadow-[0_20px_40px_-15px_rgba(126,34,206,0.5)]");
      setTextGradient("from-cyan-200 to-teal-100"); 
    } else {
      setGreeting("Good Night");
      setTheme("bg-[linear-gradient(110deg,#0f172a_0%,#1e293b_50%,#334155_100%)] shadow-[0_20px_40px_-15px_rgba(15,23,42,0.7)]");
      setTextGradient("from-cyan-400 to-blue-400"); 
    }
  }, []);

  // 🔥 2. OpenWeatherMap API (Highest Accuracy) 🔥
  useEffect(() => {
    const fetchAllData = async () => {
      const fetchWeather = async () => {
        try {
          const apiKey = "bd5e378503939ddaee76f12ad7a97608"; 
          const res = await fetch(`https://api.openweathermap.org/data/2.5/weather?lat=12.0261&lon=75.2644&units=metric&appid=${apiKey}`);
          
          if (!res.ok) throw new Error("Weather API Failed");
          
          const data = await res.json();
          
          const temp = Math.round(data.main.temp);
          const weatherId = data.weather[0].id;
          const desc = data.weather[0].main; 
          
          const hour = new Date().getHours();
          const isDay = hour >= 6 && hour < 18;
          
          let weatherIcon = isDay ? Sun : Moon; 
          let anim = isDay ? { rotate: 360 } : { y: [-2, 2, -2] }; 
          let trans = isDay ? { duration: 20, repeat: Infinity, ease: "linear" } : { duration: 4, repeat: Infinity, ease: "easeInOut" };

          if (weatherId >= 200 && weatherId < 300) { 
            weatherIcon = CloudLightning;
            anim = { scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }; trans = { duration: 1.5, repeat: Infinity, ease: "easeInOut" };
          } else if (weatherId >= 300 && weatherId < 600) { 
            weatherIcon = CloudRain;
            anim = { y: [-3, 3, -3] }; trans = { duration: 2.5, repeat: Infinity, ease: "easeInOut" };
          } else if (weatherId >= 700 && weatherId < 800) { 
            weatherIcon = Cloud;
            anim = { opacity: [0.5, 1, 0.5] }; trans = { duration: 4, repeat: Infinity, ease: "easeInOut" };
          } else if (weatherId === 800) { 
            weatherIcon = isDay ? Sun : Moon;
          } else if (weatherId > 800) { 
            weatherIcon = isDay ? CloudSun : Cloud;
            anim = { y: [-2, 2, -2] }; trans = { duration: 4, repeat: Infinity, ease: "easeInOut" };
          }

          setWeather({ temp: `${temp}°C`, desc: desc, icon: weatherIcon, anim, trans });
        } catch (error) {
          console.error("Weather error", error);
          setWeather({ temp: "--°C", desc: "Unavailable", icon: Cloud, anim: {}, trans: {} });
        }
      };

      const fetchFirebase = async () => {
        try {
          const [donorsSnap, laboursSnap, driversSnap] = await Promise.all([
            getDocs(collection(db, "donors")),
            getDocs(collection(db, "labours")),
            getDocs(collection(db, "drivers"))
          ]);

          const donorsList = donorsSnap.docs.map(doc => ({ name: `${doc.data().name} (${doc.data().blood}) - ${doc.data().place}`, category: "Blood Donors", path: "/donors" }));
          const laboursList = laboursSnap.docs.map(doc => ({ name: `${doc.data().name} (${doc.data().category}) - ${doc.data().place}`, category: "Labours", path: "/labours" }));
          const driversList = driversSnap.docs.map(doc => ({ name: `${doc.data().name} (${doc.data().vehicleType}) - ${doc.data().place}`, category: "Drivers", path: "/drivers" }));

          setDynamicData([...donorsList, ...laboursList, ...driversList]);
        } catch (error) {
          console.error("Firebase error", error);
        }
      };

      fetchWeather();
      fetchFirebase();
    };

    fetchAllData();
  }, []);

  const staticData = [
    { name: "മാടായിപ്പാറ (Madayipara)", category: "Tourism", path: "/tourism" },
    { name: "വയലപ്ര പരപ്പ് (Vayalapra Floating Park)", category: "Tourism", path: "/tourism" },
    { name: "ചൂട്ടാട് ബീച്ച് (Chootad Beach)", category: "Tourism", path: "/tourism" },
    { name: "മാടായി പള്ളി (Madayi Palli)", category: "Tourism", path: "/tourism" },
    { name: "തിരുവർക്കാട്ട് ഭഗവതി ക്ഷേത്രം (Madayi Kavu)", category: "Tourism", path: "/tourism" },
    { name: "വടുകുന്ദ ശിവക്ഷേത്രം (Vadukunda Shiva Temple)", category: "Tourism", path: "/tourism" },
    { name: "ജൂതക്കുളം (Jew Pond)", category: "Tourism", path: "/tourism" },
    { name: "മാടായി കോട്ട (Madayi Kotta)", category: "Tourism", path: "/tourism" },
    { name: "പഴയങ്ങാടി (Payangadi)", category: "Places", path: "/places" },
    { name: "കല്യാശ്ശേരി (Kalliasseri)", category: "Places", path: "/places" },
    { name: "ചെറുകുന്ന് (Cherukunnu)", category: "Places", path: "/places" },
    { name: "മാട്ടൂൽ (Mattool)", category: "Places", path: "/places" },
    { name: "പുതിയങ്ങാടി (Puthiyangadi)", category: "Places", path: "/places" },
    { name: "കണ്ണപുരം (Kannapuram)", category: "Places", path: "/places" },
    { name: "പാപ്പിനിശ്ശേരി (Pappinisseri)", category: "Places", path: "/places" },
    { name: "ചെറുതാഴം (Cheruthazham)", category: "Places", path: "/places" },
    { name: "വേങ്ങര (Vengara)", category: "Places", path: "/places" },
    { name: "ഇരിണാവ് (Irinave)", category: "Places", path: "/places" },
    { name: "താവം (Thavam)", category: "Places", path: "/places" },
    { name: "ഏരിപുരം (Eripuram)", category: "Places", path: "/places" },
    { name: "മുട്ടം (Muttam)", category: "Places", path: "/places" },
    { name: "പിലാത്തറ (Pilathara)", category: "Places", path: "/places" },
    { name: "നെരുവമ്പ്രം (Neruvambram)", category: "Places", path: "/places" },
    { name: "കണ്ണോം (Kannom)", category: "Places", path: "/places" },
    { name: "കൊട്ടില (Kottila)", category: "Places", path: "/places" },
    { name: "അതിയടം (Athiyadam)", category: "Places", path: "/places" },
    { name: "ചെങ്ങൽ (Chengal)", category: "Places", path: "/places" },
    { name: "കടന്നപ്പള്ളി (Kadannappalli)", category: "Places", path: "/places" },
    { name: "അടുത്തില (Aduthila)", category: "Places", path: "/places" },
    { name: "വയലപ്ര (Vayalapra)", category: "Places", path: "/places" },
    { name: "കുഞ്ഞിമംഗലം (Kunhimangalam)", category: "Places", path: "/places" },
    { name: "പട്ടുവം (Pattuvam)", category: "Places", path: "/places" },
    { name: "കോട്ടക്കീൽ (Kottakkeel)", category: "Places", path: "/places" },
    { name: "എഴിലോട് (Ezhilode)", category: "Places", path: "/places" },
    { name: "ശ്രീസ്ഥ (Sreestha)", category: "Places", path: "/places" },
    { name: "എടാട്ട് (Edat)", category: "Places", path: "/places" },
    { name: "കല്ല്യാശ്ശേരി ഗ്രാമപഞ്ചായത്ത് (Kalliasseri Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "കണ്ണപുരം ഗ്രാമപഞ്ചായത്ത് (Kannapuram Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "ചെറുകുന്ന് ഗ്രാമപഞ്ചായത്ത് (Cherukunnu Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "മാടായി ഗ്രാമപഞ്ചായത്ത് (Madayi Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "മാട്ടൂൽ ഗ്രാമപഞ്ചായത്ത് (Mattool Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "ഏഴോം ഗ്രാമപഞ്ചായത്ത് (Ezhome Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "ചെറുതാഴം ഗ്രാമപഞ്ചായത്ത് (Cheruthazham Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "കുഞ്ഞിമംഗലം ഗ്രാമപഞ്ചായത്ത് (Kunhimangalam Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "കടന്നപ്പള്ളി-പാണപ്പുഴ (Kadannappalli-Panapuzha)", category: "Administration", path: "/panchayath" },
    { name: "പട്ടുവം ഗ്രാമപഞ്ചായത്ത് (Pattuvam Panchayath)", category: "Administration", path: "/panchayath" },
    { name: "സാന്ത്വന കേന്ദ്രങ്ങൾ (Charity Homes)", category: "Charity", path: "/charity" },
    { name: "മിനാർ - ദി പീപ്പിൾസ് വില്ലേജ് (Minar The People's Village)", category: "Charity", path: "/charity" },
    { name: "ഹോപ്പ് റീഹാബിലിറ്റേഷൻ സെന്റർ (HOPE Rehabilitation Center)", category: "Charity", path: "/charity" },
    { name: "ഗാർഡിയൻ ഏഞ്ചൽസ് കെയർ ഹോം (Guardian Angels Care Home)", category: "Charity", path: "/charity" },
    { name: "ആശുപത്രികൾ (Hospitals)", category: "Health", path: "/hospitals" },
    { name: "അത്യാവശ്യ നമ്പറുകൾ (Emergency Contacts)", category: "Emergency", path: "/emergency" },
    { name: "സ്കൂളുകൾ (All Schools)", category: "Education", path: "/schools" },
    { name: "GBHSS CHERUKUNNU", category: "Education", path: "/schools" },
    { name: "GVHSS CHERUKUNN", category: "Education", path: "/schools" },
    { name: "CHMKSGHSS MATTUL", category: "Education", path: "/schools" },
    { name: "NAJATH GIRLS HS MATTUL", category: "Education", path: "/schools" },
    { name: "GBVHSS MADAYI", category: "Education", path: "/schools" },
    { name: "GHSS KOTTILA", category: "Education", path: "/schools" },
    { name: "GHSS CHERUTHAZHAM", category: "Education", path: "/schools" },
    { name: "GGHSS MADAYI", category: "Education", path: "/schools" },
    { name: "JAMA-ATH HS PUTHIYANGADI", category: "Education", path: "/schools" },
    { name: "GHSS KUNHIMANGALAM", category: "Education", path: "/schools" },
    { name: "GHSS KADANNAPPALLY", category: "Education", path: "/schools" },
    { name: "GWHSS CHERUKUNNU", category: "Education", path: "/schools" },
    { name: "WADIHUDA HS PAYANGADI", category: "Education", path: "/schools" },
    { name: "RAHMANIYA HS MUTTAM", category: "Education", path: "/schools" },
    { name: "TECHNICAL HS NERUVAMBRAM", category: "Education", path: "/schools" },
    { name: "MECA PAZHAYANGADI", category: "Education", path: "/schools" },
    { name: "BAKHITHA ENGLISH MEDIUM HS", category: "Education", path: "/schools" },
    { name: "MEDICAL COLLEGE PUBLIC SCHOOL", category: "Education", path: "/schools" },
    { name: "KARUNYANIKETHAN FOR THE DEAF", category: "Education", path: "/schools" },
    { name: "GMUPS MADAYI", category: "Education", path: "/schools" },
    { name: "GMUPS PAYANGADI", category: "Education", path: "/schools" },
    { name: "GWUPS VENGARA", category: "Education", path: "/schools" },
    { name: "VENGARA MAPPILA UPS", category: "Education", path: "/schools" },
    { name: "KANNAPURAM EAST UPS", category: "Education", path: "/schools" },
    { name: "ODAYAMMADAM UPS", category: "Education", path: "/schools" },
    { name: "GMUPS THEKKUMBAD", category: "Education", path: "/schools" },
    { name: "MRUPS MATTUL", category: "Education", path: "/schools" },
    { name: "LFUPS MATTUL", category: "Education", path: "/schools" },
    { name: "GMUPS EZHOME", category: "Education", path: "/schools" },
    { name: "GCUPS KUNHIMANGALAM", category: "Education", path: "/schools" },
    { name: "KADANNAPPALLY UPS", category: "Education", path: "/schools" },
    { name: "VENGARA PRIYADARSHINI UPS", category: "Education", path: "/schools" },
    { name: "EDAKKEPPURAM UPS", category: "Education", path: "/schools" },
    { name: "PILATHARA UPS", category: "Education", path: "/schools" },
    { name: "CENTRAL MUSLIM LPS MATTUL", category: "Education", path: "/schools" },
    { name: "CHERUKKUNNU MUSLIM LPS", category: "Education", path: "/schools" },
    { name: "SANKARAVILASAM A.L.P.S", category: "Education", path: "/schools" },
    { name: "EDAKKEPPURAM LPS", category: "Education", path: "/schools" },
    { name: "EDANAD EAST LPS", category: "Education", path: "/schools" },
    { name: "EZHOME HINDU LPS", category: "Education", path: "/schools" },
    { name: "GLPS CHERUKKUNNU NORTH", category: "Education", path: "/schools" },
    { name: "GLPS CHERUTHAZHAM SOUTH", category: "Education", path: "/schools" },
    { name: "GLPS KUNHIMANGALAM", category: "Education", path: "/schools" },
    { name: "GMLPS MADAKKARA", category: "Education", path: "/schools" },
    { name: "GMLPS MATTUL", category: "Education", path: "/schools" },
    { name: "GWLPS EZHOME", category: "Education", path: "/schools" },
    { name: "KANNOM LPS", category: "Education", path: "/schools" },
    { name: "MADAYI LPS", category: "Education", path: "/schools" },
    { name: "MATTUL ALPS", category: "Education", path: "/schools" },
    { name: "DEVIVILASAM LPS THAVAM", category: "Education", path: "/schools" },
    { name: "MUTTIL LPS", category: "Education", path: "/schools" },
    { name: "ONAPPARAMBA LPS", category: "Education", path: "/schools" },
    { name: "PUTHIYANGADI WEST LPS", category: "Education", path: "/schools" },
    { name: "SRVLPS CHERUTHAZHAM", category: "Education", path: "/schools" },
    { name: "ST. MARYS LPS VILAYANKODE", category: "Education", path: "/schools" },
    { name: "ST. MARYS LPS PUNNACHERI", category: "Education", path: "/schools" },
    { name: "KANNAPURAM NORTH LPS", category: "Education", path: "/schools" },
    { name: "MADAYI SOUTH LPS", category: "Education", path: "/schools" },
    { name: "16649 - Parasuram Express (06:41 AM)", category: "Train Timings", path: "/trains" },
    { name: "16610 - MAQ Kozhikode Exp (07:11 AM)", category: "Train Timings", path: "/trains" },
    { name: "16345 - Netravati Express (07:42 AM)", category: "Train Timings", path: "/trains" },
    { name: "16160 - Chennai Egmore Exp (08:35 AM)", category: "Train Timings", path: "/trains" },
    { name: "16605 - Ernad Express (09:11 AM)", category: "Train Timings", path: "/trains" },
    { name: "16324 - Mangaluru CBE Exp (11:14 AM)", category: "Train Timings", path: "/trains" },
    { name: "12602 - Mangaluru Chennai Mail (03:35 PM)", category: "Train Timings", path: "/trains" },
    { name: "16348 - Mangaluru TVC Exp (04:25 PM)", category: "Train Timings", path: "/trains" },
    { name: "16603 - Maveli Express (07:15 PM)", category: "Train Timings", path: "/trains" },
    { name: "16630 - Malabar Express (08:25 PM)", category: "Train Timings", path: "/trains" },
    { name: "16604 - Maveli Express (05:20 AM)", category: "Train Timings", path: "/trains" },
    { name: "16629 - Malabar Express (07:05 AM)", category: "Train Timings", path: "/trains" },
    { name: "16347 - TVC Mangaluru Exp (08:23 AM)", category: "Train Timings", path: "/trains" },
    { name: "12601 - Chennai Mangaluru Mail (09:35 AM)", category: "Train Timings", path: "/trains" },
    { name: "22610 - Intercity SF Exp (11:03 AM)", category: "Train Timings", path: "/trains" },
    { name: "16606 - Ernad Express (02:37 PM)", category: "Train Timings", path: "/trains" },
    { name: "16323 - Coimbatore MAQ Exp (03:42 PM)", category: "Train Timings", path: "/trains" },
    { name: "16159 - Chennai Mangaluru Exp (04:48 PM)", category: "Train Timings", path: "/trains" },
    { name: "16650 - Parasuram Express (06:29 PM)", category: "Train Timings", path: "/trains" },
    { name: "12617 - Mangala Lakshadweep (07:03 PM)", category: "Train Timings", path: "/trains" },
    { name: "പഴയങ്ങാടി റെയിൽവേ സ്റ്റേഷൻ (Payangadi Railway Station)", category: "Train Timings", path: "/trains" }
  ];

  const allSearchData = [...staticData, ...dynamicData];
  const filteredResults = searchQuery.trim() === "" 
    ? [] 
    : allSearchData.filter((item) => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.category.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const WeatherIcon = weather.icon;

  return (
    <>
      <Helmet>
        <title>Info Payangadi | Kalliasseri Digital Directory</title>
        <meta name="description" content="Explore Kalliasseri constituency. Find blood donors, workers, drivers, tourism spots, and essential services in one place." />
      </Helmet>

      {/* 🔥 COPY PREVENTION (SEO Safe) 🔥 */}
      <div 
        className="min-h-[100dvh] w-full overflow-x-hidden bg-[#F8FAFC] pb-36 selection:bg-blue-200 relative select-none"
        onCopy={(e) => {
          e.preventDefault();
          toast.error("Copying is disabled for this content!");
        }}
        onContextMenu={(e) => e.preventDefault()}
      >

        <AnimatePresence>
          {searchQuery && (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1, backdropFilter: "blur(12px)" }} 
              exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-slate-900/40" 
              onClick={() => setSearchQuery("")}
            />
          )}
        </AnimatePresence>

        {/* 🌟 2. PREMIUM HEADER SECTION 🌟 */}
        <div className={`${theme} rounded-b-[3rem] px-6 pt-14 pb-36 relative transition-colors duration-500 ${searchQuery ? 'z-50' : 'z-10'}`}>
          
          <div className="absolute inset-0 overflow-hidden rounded-b-[3rem] pointer-events-none">
            <div className="absolute top-[-20%] right-[-10%] w-[300px] h-[300px] bg-white/10 rounded-full blur-3xl transition-opacity duration-500"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[250px] h-[250px] bg-white/5 rounded-full blur-3xl transition-opacity duration-500"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center">
            
            <motion.div 
              initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
              className="flex items-center justify-between bg-white/10 backdrop-blur-md px-5 py-2 rounded-full border border-white/10 w-max mb-5 absolute -top-8 shadow-sm"
            >
              <div className="flex items-center gap-1.5 text-white font-medium text-xs sm:text-sm border-r border-white/20 pr-3 mr-3">
                <span className="opacity-90 tracking-wide">{greeting}, Payangadi</span>
              </div>
              <div className="flex items-center gap-1.5 text-white font-bold text-xs sm:text-sm tracking-wide">
                <motion.div animate={weather.anim} transition={weather.trans} style={{ display: "inline-block" }}>
                  <WeatherIcon size={16} className="text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.6)]" />
                </motion.div>
                {weather.temp} <span className="hidden sm:inline font-normal opacity-80 text-[11px] ml-0.5">({weather.desc})</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}
              className="text-4xl sm:text-5xl md:text-6xl font-black text-white text-center tracking-tight leading-tight mt-8"
            >
              Discover <span className={`text-transparent bg-clip-text bg-gradient-to-r ${textGradient} drop-shadow-md transition-all duration-500`}>Everything</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              className="text-white/90 font-medium text-sm sm:text-base mt-4 max-w-lg text-center tracking-wide"
            >
              കല്യാശ്ശേരി മണ്ഡലത്തിന്റെ സമ്പൂർണ്ണ ഡിജിറ്റൽ ഡയറക്ടറി
            </motion.p>

            {/* 🔍 THE MASTER SEARCH BAR 🔍 */}
            <div className={`w-full mt-8 max-w-2xl relative z-50 transition-transform duration-300 ease-out ${searchQuery ? '-translate-y-8 sm:-translate-y-12' : 'translate-y-0'}`}>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
                className="relative flex items-center group"
              >
                <Search size={22} className={`absolute left-5 sm:left-6 transition-colors duration-200 pointer-events-none z-10 ${searchQuery ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-500'}`} />
                
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  placeholder="സ്ഥലങ്ങൾ, സ്കൂളുകൾ, സേവനങ്ങൾ..."
                  className="w-full pl-14 sm:pl-16 pr-12 py-4 sm:py-[1.15rem] rounded-full text-slate-800 bg-white/95 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] outline-none border border-white/50 focus:border-blue-300 focus:ring-[4px] focus:ring-blue-500/20 focus:bg-white transition-all font-semibold text-sm sm:text-base placeholder:text-sm placeholder:font-medium placeholder:text-gray-400"
                />
                
                <AnimatePresence>
                  {searchQuery && (
                    <motion.button 
                      initial={{ opacity: 0, scale: 0.5 }} 
                      animate={{ opacity: 1, scale: 1 }} 
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.15 }}
                      onClick={() => setSearchQuery("")} 
                      className="absolute right-3 sm:right-4 text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-all"
                    >
                      <X size={18} strokeWidth={2.5} />
                    </motion.button>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* 🎯 Search Results Dropdown 🎯 */}
              <AnimatePresence>
                {searchQuery.trim() !== "" && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-[calc(100%+12px)] left-0 right-0 bg-white/80 backdrop-blur-2xl rounded-3xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden text-left max-h-[60vh] overflow-y-auto border border-white custom-scrollbar z-50"
                  >
                    {filteredResults.length > 0 ? (
                      <div className="p-2 sm:p-3">
                        {Object.entries(
                          filteredResults.reduce((acc, result) => {
                            if (!acc[result.category]) acc[result.category] = [];
                            acc[result.category].push(result);
                            return acc;
                          }, {})
                        ).map(([category, items], catIndex) => {
                          const style = categoryStyles[category] || categoryStyles.Default;
                          const CatIcon = style.icon;

                          return (
                            <div key={catIndex} className="mb-3 last:mb-1">
                              <div className={`mx-2 mt-1 px-3 py-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 rounded-lg ${style.bg} ${style.color} border ${style.border} w-max`}>
                                <CatIcon size={14} strokeWidth={2.5} /> {category}
                              </div>
                              <div className="mt-1.5">
                                {items.map((result, index) => (
                                  <div
                                    key={index}
                                    onClick={() => navigate(result.path)}
                                    className="mx-1 px-3 py-3 rounded-2xl hover:bg-white/90 hover:shadow-sm cursor-pointer flex items-center justify-between group transition-all duration-200"
                                  >
                                    <h4 className="text-slate-700 font-bold text-sm group-hover:text-blue-600 transition-colors">{result.name}</h4>
                                    <div className="bg-slate-100 p-1.5 rounded-full shadow-sm group-hover:bg-blue-600 group-hover:text-white text-slate-400 transition-all duration-300 group-hover:translate-x-1">
                                      <ChevronRight size={14} strokeWidth={2.5} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-5 py-12 flex flex-col items-center justify-center text-center">
                        <div className="bg-slate-100 p-4 rounded-full mb-3 shadow-inner">
                          <Search size={28} className="text-slate-400" />
                        </div>
                        <h4 className="text-slate-800 font-bold text-base">No results found</h4>
                        <p className="text-slate-500 text-xs mt-1 font-medium">We couldn't find anything for "{searchQuery}"</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* 🌟 3. FLOATING CATEGORY GRID 🌟 */}
        <div className="-mt-20 relative z-30 px-4 sm:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
            {[
              { name: "Tourism", icon: Map, path: "/tourism", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
              { name: "Villages", icon: Compass, path: "/places", color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-100" },
              { name: "Trains", icon: Train, path: "/trains", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
              { name: "Drivers", icon: Car, path: "/drivers", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
              { name: "Labours", icon: Briefcase, path: "/labours", color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-100" },
              { name: "Hospitals", icon: Hospital, path: "/hospitals", color: "text-red-600", bg: "bg-red-50", border: "border-red-100" },
              { name: "Schools", icon: School, path: "/schools", color: "text-sky-600", bg: "bg-sky-50", border: "border-sky-100" },
              { name: "Charity", icon: HeartHandshake, path: "/charity", color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-100" },
              { name: "Panchayath", icon: Building2, path: "/panchayath", color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
              { name: "Emergency", icon: AlertTriangle, path: "/emergency", color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                whileHover={{ y: -4, scale: 1.01 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(item.path)}
                className="bg-white p-5 sm:p-6 rounded-[2rem] shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_15px_30px_rgb(37,99,235,0.08)] border border-slate-100 cursor-pointer flex flex-col items-center justify-center gap-3 transition-all group relative overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br from-transparent to-${item.bg.replace('bg-','')} opacity-0 group-hover:opacity-40 transition-opacity duration-300`}></div>
                <div className={`${item.bg} ${item.color} border ${item.border} w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm relative z-10`}>
                  <item.icon size={26} strokeWidth={2} />
                </div>
                <div className="font-bold text-slate-700 text-xs sm:text-sm tracking-wide group-hover:text-blue-600 transition-colors relative z-10 text-center">
                  {item.name}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 🌟 4. EMERGENCY & QUICK ACTIONS 🌟 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.01 }}
              onClick={() => navigate("/donors")}
              className="bg-gradient-to-br from-red-500 via-red-500 to-rose-600 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-8 text-white shadow-xl shadow-red-500/20 cursor-pointer relative overflow-hidden group border border-red-400"
            >
              <div className="absolute right-0 top-0 w-40 h-40 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none transition-transform duration-500 group-hover:scale-110"></div>
              <Heart size={40} strokeWidth={2.5} className="mb-4 sm:mb-5 opacity-90 group-hover:scale-110 transition-transform duration-300 drop-shadow-md" />
              <h3 className="text-xl sm:text-2xl font-black mb-1.5 tracking-tight">Blood Donors</h3>
              <p className="text-red-50 text-xs sm:text-sm font-medium leading-relaxed max-w-[85%]">Find available donors nearby or register yourself to save a life today.</p>
              <div className="mt-5 sm:mt-6 inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-md px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold transition-colors shadow-sm">
                View Directory <ChevronRight size={16} strokeWidth={3} />
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.01 }}
              onClick={() => navigate("/emergency")}
              className="bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-8 text-white shadow-xl shadow-slate-900/20 cursor-pointer relative overflow-hidden group border border-slate-700"
            >
              <div className="absolute right-0 top-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none transition-transform duration-500 group-hover:scale-110"></div>
              <AlertTriangle size={40} strokeWidth={2.5} className="mb-4 sm:mb-5 text-orange-400 group-hover:scale-110 transition-transform duration-300 drop-shadow-md" />
              <h3 className="text-xl sm:text-2xl font-black mb-1.5 tracking-tight">Emergency Help</h3>
              <p className="text-slate-300 text-xs sm:text-sm font-medium leading-relaxed max-w-[85%]">Quick access to Police, Fire force, KSEB, and nearby Hospitals.</p>
              <div className="mt-5 sm:mt-6 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-md px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold transition-colors shadow-sm border border-white/5">
                Quick Contacts <ChevronRight size={16} strokeWidth={3} />
              </div>
            </motion.div>

          </div>
        </div>

        {/* 🔥 5. NEWS & JOBS (EXTERNAL PORTALS) - HIGHLY ATTRACTIVE 🔥 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12 relative z-20">
          <div className="flex items-center justify-between mb-5 sm:mb-6 pl-1 sm:pl-2">
            <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              <Newspaper className="text-rose-500" size={24} strokeWidth={2.5}/> News & <span className="text-rose-600">Jobs</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            
            {/* LOCAL NEWS CARD */}
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.02, y: -2 }}
              onClick={() => window.open("https://payangadilive.com", "_blank", "noopener,noreferrer")}
              className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-[2rem] p-6 sm:p-8 text-white shadow-lg shadow-purple-500/25 cursor-pointer relative overflow-hidden group border border-purple-400"
            >
              <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/20 rounded-full blur-2xl pointer-events-none transition-transform duration-500 group-hover:scale-125"></div>
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <div className="bg-white/20 p-3 rounded-2xl w-max mb-4 backdrop-blur-sm border border-white/20 group-hover:bg-white/30 transition-colors">
                    <Newspaper size={28} strokeWidth={2.5} className="text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black mb-1.5 tracking-tight">Local News</h3>
                  <p className="text-purple-50 text-xs sm:text-sm font-medium leading-relaxed max-w-[90%]">പഴയങ്ങാടിയിലെയും പരിസരങ്ങളിലെയും വാർത്തകൾ അറിയാൻ</p>
                </div>
                <div className="bg-white/10 p-3 rounded-full backdrop-blur-md border border-white/10 group-hover:bg-white text-white group-hover:text-purple-600 transition-all duration-300">
                  <ChevronRight size={24} strokeWidth={3} />
                </div>
              </div>
            </motion.div>

            {/* JOB MALAYALAM CARD */}
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.02, y: -2 }}
              onClick={() => window.open("https://jobmalayalam.in", "_blank", "noopener,noreferrer")}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-[2rem] p-6 sm:p-8 text-white shadow-lg shadow-blue-500/25 cursor-pointer relative overflow-hidden group border border-blue-400"
            >
              <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/20 rounded-full blur-2xl pointer-events-none transition-transform duration-500 group-hover:scale-125"></div>
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <div className="bg-white/20 p-3 rounded-2xl w-max mb-4 backdrop-blur-sm border border-white/20 group-hover:bg-white/30 transition-colors">
                    <Briefcase size={28} strokeWidth={2.5} className="text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black mb-1.5 tracking-tight">Job Malayalam</h3>
                  <p className="text-blue-50 text-xs sm:text-sm font-medium leading-relaxed max-w-[90%]">കേരളത്തിലെ ഏറ്റവും പുതിയ തൊഴിൽ വാർത്തകൾ</p>
                </div>
                <div className="bg-white/10 p-3 rounded-full backdrop-blur-md border border-white/10 group-hover:bg-white text-white group-hover:text-blue-600 transition-all duration-300">
                  <ChevronRight size={24} strokeWidth={3} />
                </div>
              </div>
            </motion.div>

          </div>
        </div>

        {/* 🌟 6. DISCOVER MORE SECTION 🌟 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-12 sm:mt-16 mb-8 relative z-20">
          <div className="flex items-center justify-between mb-5 sm:mb-6 pl-1 sm:pl-2">
            <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              <Compass className="text-blue-500" size={24} strokeWidth={2.5}/> Discover <span className="text-blue-600">More</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: "Explore Local Villages", desc: "നാടിന്റെ ചരിത്രവും പൈതൃകവും അറിയാം", path: "/places", color: "bg-white", iconCol: "text-teal-600", bgIcon: "bg-teal-50", borderIcon: "border-teal-100" },
              { title: "Find Care Homes", desc: "സാന്ത്വന കേന്ദ്രങ്ങളുടെ വിവരങ്ങൾ", path: "/charity", color: "bg-white", iconCol: "text-rose-600", bgIcon: "bg-rose-50", borderIcon: "border-rose-100" },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => navigate(card.path)}
                className={`${card.color} p-5 sm:p-6 rounded-[2rem] flex justify-between items-center cursor-pointer border border-slate-100 shadow-sm hover:shadow-md transition-all group`}
              >
                <div>
                  <h3 className="font-black text-slate-800 text-base sm:text-lg tracking-tight group-hover:text-blue-600 transition-colors">{card.title}</h3>
                  <p className="text-[11px] sm:text-xs text-slate-500 font-medium mt-1">{card.desc}</p>
                </div>
                <div className={`${card.bgIcon} ${card.iconCol} border ${card.borderIcon} p-3 sm:p-3.5 rounded-2xl group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-300 shadow-sm`}>
                  <ChevronRight size={18} strokeWidth={3} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; padding: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; border: 2px solid white; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </>
  );
}